import type { AdminUserSummary } from '@/api';
import { formatUsd } from '@/lib/format';

const DAY_MS = 86_400_000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export type DayBucket = { day: string; count: number };

export type SortKey =
  | 'newest'
  | 'oldest'
  | 'recent-login'
  | 'identifier'
  | 'tokens';

export type UserFilter =
  | 'all'
  | 'admins'
  | 'disabled'
  | 'temporary'
  | 'never-logged-in';

type UserPopulation = {
  total: number;
  admins: number;
  disabled: number;
  temporary: number;
  neverLoggedIn: number;
  activeRecently: number;
};

/**
 * The calendar day an account was created on, as `YYYY-MM-DD`.
 *
 * A string slice, not a `Date` parse: `users.createdAt` is TEXT holding
 * server-LOCAL time with a `Z` it hasn't earned, so parsing it reads it as UTC
 * and then renders it in the viewer's zone, shifting the day twice. Null for a
 * `pending:` row, which has no `users` row and so no timestamp at all.
 */
export function accountDay(
  createdAt: string | null | undefined
): string | null {
  if (!createdAt || createdAt.length < 10) return null;
  const day = createdAt.slice(0, 10);
  return DAY_PATTERN.test(day) ? day : null;
}

/**
 * `last_login_at` as epoch milliseconds, or null when unusable.
 *
 * Unlike `createdAt` this one really is UTC (`utc_now()`), so parsing is correct.
 */
function lastLoginMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/** The viewer's local calendar day; local getters, since `toISOString` gives UTC. */
function localDay(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The day a signup series should end on: the later of today and the newest
 * account, so a signup stamped ahead of the viewer's clock stays on the chart.
 */
export function seriesEndDay(users: AdminUserSummary[], now: Date): string {
  let end = localDay(now);
  for (const user of users) {
    const day = accountDay(user.createdAt);
    if (day && day > end) end = day;
  }
  return end;
}

/**
 * Account creations per day: exactly `days` ascending buckets ending at `today`,
 * zero-filled so a bar chart keeps an evenly spaced axis.
 */
export function buildSignupSeries(
  users: AdminUserSummary[],
  days: number,
  today: string
): DayBucket[] {
  const end = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(end) || days < 1) return [];

  const counts = new Map<string, number>();
  for (const user of users) {
    const day = accountDay(user.createdAt);
    if (day) counts.set(day, (counts.get(day) ?? 0) + 1);
  }

  const series: DayBucket[] = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const day = new Date(end - offset * DAY_MS).toISOString().slice(0, 10);
    series.push({ day, count: counts.get(day) ?? 0 });
  }
  return series;
}

/**
 * Population slices for the overview cards.
 *
 * "New in the last N days" is deliberately absent — it comes from summing
 * `buildSignupSeries`, so the card and the chart can never disagree.
 */
export function summarizeUsers(
  users: AdminUserSummary[],
  now: number,
  activeDays = 7
): UserPopulation {
  const activeCutoff = now - activeDays * DAY_MS;
  const population: UserPopulation = {
    total: users.length,
    admins: 0,
    disabled: 0,
    temporary: 0,
    neverLoggedIn: 0,
    activeRecently: 0
  };

  for (const user of users) {
    if (user.role === 'admin') population.admins += 1;
    if (user.disabled) population.disabled += 1;
    if (user.is_temporary) population.temporary += 1;
    const seen = lastLoginMs(user.last_login_at);
    if (seen === null) population.neverLoggedIn += 1;
    else if (seen >= activeCutoff) population.activeRecently += 1;
  }
  return population;
}

/**
 * Cost cell for a user row: an em-dash when any of their models had no price.
 *
 * `cost_for` returns 0.0 for an unpriced model, so a raw `formatUsd` would show
 * "$0" for a heavy user whose model simply isn't in `models.yaml` — the same
 * thing it shows for someone who genuinely spent nothing. Most entries carry no
 * `cost`, so that case is the common one, not the edge one.
 */
export function formatUserCost(user: AdminUserSummary): string {
  if (user.pricing_missing) return '—';
  return formatUsd(user.total_cost ?? 0);
}

/** Order two ISO stamps, missing values last. */
function byStamp(
  a: string | null | undefined,
  b: string | null | undefined,
  newestFirst: boolean
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  if (a === b) return 0;
  const ascending = a < b ? -1 : 1;
  return newestFirst ? -ascending : ascending;
}

/** Comparator for the user list; identifier is the stable tiebreak. */
export function compareUsers(
  a: AdminUserSummary,
  b: AdminUserSummary,
  sortKey: SortKey
): number {
  const tiebreak = a.identifier.localeCompare(b.identifier);
  switch (sortKey) {
    case 'oldest':
      return byStamp(a.createdAt, b.createdAt, false) || tiebreak;
    case 'recent-login':
      return byStamp(a.last_login_at, b.last_login_at, true) || tiebreak;
    case 'identifier':
      return tiebreak;
    case 'tokens':
      return (b.total_tokens ?? 0) - (a.total_tokens ?? 0) || tiebreak;
    case 'newest':
    default:
      return byStamp(a.createdAt, b.createdAt, true) || tiebreak;
  }
}

/** Whether a user belongs to the selected quick-filter bucket. */
function matchesFilter(user: AdminUserSummary, filter: UserFilter): boolean {
  switch (filter) {
    case 'admins':
      return user.role === 'admin';
    case 'disabled':
      return Boolean(user.disabled);
    case 'temporary':
      return Boolean(user.is_temporary);
    case 'never-logged-in':
      return lastLoginMs(user.last_login_at) === null;
    case 'all':
    default:
      return true;
  }
}

/** Case-insensitive substring match over the identifier and provider. */
function matchesSearch(user: AdminUserSummary, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    user.identifier.toLowerCase().includes(needle) ||
    (user.provider ?? '').toLowerCase().includes(needle)
  );
}

/**
 * Search, filter, and sort the list in one pass.
 *
 * Client-side because `GET /admin/users` returns every user unpaginated, so
 * there is nothing to refetch. `.filter()` copies first, so the sort never
 * mutates the Recoil-held array.
 */
export function visibleUsers(
  users: AdminUserSummary[],
  {
    query,
    filter,
    sortKey
  }: { query: string; filter: UserFilter; sortKey: SortKey }
): AdminUserSummary[] {
  return users
    .filter((user) => matchesFilter(user, filter) && matchesSearch(user, query))
    .sort((a, b) => compareUsers(a, b, sortKey));
}
